import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'react-hot-toast';
import { Mail, CheckCircle, XCircle, AlertCircle, Info } from 'lucide-react';
import { AuthContext } from '../App';

const ConfigFormularioRegistraciones = () => {
  const { API } = useContext(AuthContext);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actualizando, setActualizando] = useState(false);

  useEffect(() => {
    // FORZAR reset del estado actualizando al montar
    setActualizando(false);
    cargarConfiguracion();
  }, []);

  const cargarConfiguracion = async () => {
    try {
      setLoading(true);
      setActualizando(false); // FORZAR reset
      const response = await axios.get(`${API}/admin/formulario-registraciones-config`);
      setConfig(response.data);
    } catch (error) {
      console.error('Error cargando configuración:', error);
      toast.error('Error al cargar configuración');
    } finally {
      setLoading(false);
      setActualizando(false); // DOBLE seguridad
    }
  };

  const cambiarEstado = async (nuevoEstado) => {
    console.log('🔵 cambiarEstado llamado con:', nuevoEstado);
    
    const accion = nuevoEstado ? 'activar' : 'desactivar';
    const confirmado = window.confirm(`¿${accion.charAt(0).toUpperCase() + accion.slice(1)} el formulario de consultas en la landing page?`);
    
    console.log('🔵 Confirmación:', confirmado);
    
    if (!confirmado) {
      console.log('🔵 Usuario canceló');
      return;
    }

    console.log('🔵 Iniciando petición...');
    setActualizando(true);
    
    try {
      const url = `${API}/admin/formulario-registraciones-config?activo=${nuevoEstado}`;
      console.log('🔵 URL:', url);
      
      const response = await axios.put(url);
      console.log('🔵 Respuesta:', response.data);
      
      toast.success(response.data.message);
      await cargarConfiguracion();
    } catch (error) {
      console.error('🔴 Error completo:', error);
      console.error('🔴 Error response:', error.response);
      toast.error(error.response?.data?.detail || 'Error al actualizar configuración');
    } finally {
      console.log('🔵 Finalizando...');
      setActualizando(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  const estadoActual = config?.activo;

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader className="bg-gradient-to-r from-purple-600 to-blue-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">Formulario de Consultas - Landing Page</h2>
              <p className="text-purple-100">Control de período de registraciones</p>
            </div>
            <Mail className="w-12 h-12 text-white opacity-80" />
          </div>
        </CardHeader>

        <CardContent className="p-8">
          {/* Estado Actual */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-800">Estado Actual</h3>
              {estadoActual ? (
                <Badge className="bg-green-100 text-green-800 border-green-300 border text-lg px-4 py-2">
                  <CheckCircle className="w-5 h-5 mr-2 inline" />
                  ACTIVO
                </Badge>
              ) : (
                <Badge className="bg-red-100 text-red-800 border-red-300 border text-lg px-4 py-2">
                  <XCircle className="w-5 h-5 mr-2 inline" />
                  INACTIVO
                </Badge>
              )}
            </div>

            {estadoActual ? (
              <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
                <div className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-green-600 mr-3 mt-1" />
                  <div>
                    <p className="text-green-800 font-semibold">El formulario está VISIBLE en la landing page</p>
                    <p className="text-green-700 text-sm mt-1">
                      Los visitantes pueden enviar consultas y solicitar información de registro.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
                <div className="flex items-start">
                  <XCircle className="w-6 h-6 text-red-600 mr-3 mt-1" />
                  <div>
                    <p className="text-red-800 font-semibold">El formulario está OCULTO en la landing page</p>
                    <p className="text-red-700 text-sm mt-1">
                      Los visitantes NO pueden enviar consultas. El formulario no aparece en la página.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Información */}
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded mb-8">
            <div className="flex items-start">
              <Info className="w-6 h-6 text-blue-600 mr-3 mt-1 flex-shrink-0" />
              <div>
                <p className="text-blue-800 font-semibold mb-2">¿Cómo funciona?</p>
                <ul className="text-blue-700 text-sm space-y-1 list-disc list-inside">
                  <li>Cuando está <strong>ACTIVO</strong>: El formulario aparece en la landing page antes del footer</li>
                  <li>Cuando está <strong>INACTIVO</strong>: El formulario no se muestra en absoluto</li>
                  <li>Úsalo para abrir/cerrar períodos de registraciones según tus necesidades</li>
                  <li>Las consultas recibidas siempre quedan guardadas en "Consultas de Visitantes"</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Acciones */}
          <div className="flex gap-4">
            {estadoActual ? (
              <Button
                onClick={() => cambiarEstado(false)}
                disabled={actualizando}
                variant="destructive"
                size="lg"
                className="flex-1 h-14 text-lg"
              >
                {actualizando ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Desactivando...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <XCircle className="w-6 h-6 mr-2" />
                    Desactivar Formulario
                  </span>
                )}
              </Button>
            ) : (
              <Button
                onClick={() => cambiarEstado(true)}
                disabled={actualizando}
                className="flex-1 h-14 text-lg bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700"
              >
                {actualizando ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Activando...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 mr-2" />
                    Activar Formulario
                  </span>
                )}
              </Button>
            )}
          </div>

          {/* Info adicional */}
          {config?.ultima_actualizacion && (
            <p className="text-center text-sm text-gray-500 mt-6">
              Última actualización: {new Date(config.ultima_actualizacion).toLocaleString('es-UY')}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Advertencia */}
      <Card className="border-orange-300">
        <CardContent className="p-6">
          <div className="flex items-start">
            <AlertCircle className="w-6 h-6 text-orange-600 mr-3 mt-1 flex-shrink-0" />
            <div>
              <p className="text-orange-800 font-semibold mb-2">Importante</p>
              <p className="text-orange-700 text-sm">
                Cuando desactives el formulario, los visitantes no podrán solicitar información. 
                Úsalo estratégicamente durante tus períodos de inscripción abiertos.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ConfigFormularioRegistraciones;
